import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonTextSizes,
  buttonFunctions,
} from '../../shared/Button';
import Table from '../../shared/table/Table';
import { selectorTypes } from '../../shared/table/TableRow';
import SvgXIcon from '../../svg/XIcon';
import {
  searchText,
  submitText,
  unitNameText,
  cancelText,
  selectedUnitsText,
} from '../../shared/SharedInternationalizations';
import InlineHelpText from '../../shared/InlineHelpText';
import HelpIcon from '../../shared/HelpIcon';

/**
 * Component that allows bulk associating of units and users.
 * @param {Function} props.onSubmit - function to be called when the form is submitted
 * @param {array} props.unitArray - Array of all of the units
 * @param {string} props.unit - the unit id for the unit that is being associated with
 * @param {array} props.initialUnits - the array of units already set as child units for this unit
 */
export function UnconnectedBulkEditChildUnitsForm(props) {
  const { onSubmit, unitArray, unit, initialUnits, unitObj } = props;
  const pathArray = unitObj.path.split(',');
  const pathArrInt = pathArray.map((i) => parseInt(i));

  const [unitsFilter, setUnitsFilter] = useState('');

  const [showSelectedUnitsHelpText, setShowSelectedUnitsHelpText] = useState(
    false
  );
  const selectedUnitsHelpTextRef = useRef();
  function toggleSelectedUnitsHelpText(value) {
    setShowSelectedUnitsHelpText(value);
  }
  const selectedUnitsHelpText = (
    <FormattedMessage
      id="bulkAssociateParentUnitForm.selectedUnitsHelpText"
      defaultMessage="The units that have been selected. To remove a unit from this list, click the 'x' icon. To add a unit, click in the check box."
      description="Help text."
    />
  );

  /**
   * Function to set the search value for active filtering for units
   * @param {object} event - The event from typing in the value to the search bar
   * @fires setUnitsFilter - Updates local state with the value typed into the search bar
   */
  const unitsSearchFilter = (event) => {
    const value = event.target.value;
    setUnitsFilter(value);
  };

  /**
   * Function to generate the units table
   * @returns {object} - The table of units
   */
  const unitsTable = (values) => {
    // filter out any units in the current unit's path to ensure that the unit doesn't add one of the units above it as a child
    const initiallyFilteredUnits = unitArray.filter((item) => {
      if (pathArrInt.includes(item.id)) {
        return false;
      } else if (!item.is_active) {
        return false;
      } else if (values.unitIds.includes(item.id)) {
        return false;
      } else {
        return true;
      }
    });

    // map over the units and put into the format that is required for the table component
    const unitsArray = initiallyFilteredUnits.map((unit) => {
      let checked = null;

      return {
        selector: {
          checked: checked,
          sortBy: unit.id,
          display: unit.id,
        },
        name: {
          sortBy: unit.name.toUpperCase(),
          display: unit.name,
        },
      };
    });

    let searchedUnits = [];
    // Take the array and filter based on the text entered in the search bar.
    if (unitsFilter !== '') {
      searchedUnits = unitsArray.filter((item) => {
        // Make the device name uppercase for case insensitivity
        const units = item.name.display.toUpperCase();
        // filter based off the includes() value with the entered text (also switched to uppercase) as the filter parameter
        return units.includes(unitsFilter.toUpperCase());
      });
    } else {
      searchedUnits = unitsArray;
    }

    const tableHeadings = [
      {
        isSortable: false,
        display: '',
      },
      {
        isSortable: true,
        sortBy: 'name',
        display: unitNameText,
      },
    ];
    // generates the actual table
    const table = (
      <Table
        hasTableHead={true}
        hasSelector={true}
        tableHeadings={tableHeadings}
        selectorName="unitIds"
        selectorParamValue="id"
        sortParam="name"
        tableData={searchedUnits}
        selectorType={selectorTypes.checkbox}
        tableStyles="table__form-selector-table"
        data-test="bulk-associate-child-units-table"
      />
    );

    return table;
  };

  /**
   * Function to generate the parent unit table
   * @param {object} values - the current values of the form
   * @returns {object} - The table of parent units
   */
  const selectedUnitsTable = (values) => {
    let table;
    let unitIds = values.unitIds;
    let unitRows;
    if (unitIds.length > 0) {
      if (unitIds) {
        const filteredUnits = unitIds.map((id) => {
          let thisId = id;
          if (typeof id === 'string') {
            thisId = parseInt(unitIds);
          }
          const userId = unitArray.filter((unit) => unit.id === thisId);
          return userId[0];
        });
        unitRows = filteredUnits.map((unit) => {
          const removeButton = (
            <label>
              <Field
                className="visually-hidden"
                name="unitIds"
                component="input"
                type="checkbox"
                value={unit.id}
              />
              <SvgXIcon />
            </label>
          );
          return {
            button: {
              sortBy: '',
              display: removeButton,
            },
            name: {
              sortBy: unit.name.toUpperCase(),
              display: unit.name,
            },
          };
        });
      }

      const tableHeadings = [
        {
          isSortable: false,
          display: '',
        },
        {
          isSortable: true,
          sortBy: 'name',
          display: unitNameText,
        },
      ];
      // generates the actual table
      table = (
        <Table
          hasTableHead={true}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="name"
          tableData={unitRows}
          tableStyles="table__form-selector-table"
          data-test="bulk-associate-child-units-selected-unit-table"
        />
      );

      return table;
    } else {
      return '';
    }
  };

  return (
    <React.Fragment>
      <div className="associate-users-units__message-container" />
      <Form
        data-test="bulk-associate-child-units-form"
        onSubmit={onSubmit}
        initialValues={{
          unit: unit,
          unitIds: initialUnits || [],
          initialUnits: initialUnits || [],
        }}
        render={({
          handleSubmit,
          pristine,
          invalid,
          reset,
          submitting,
          values,
        }) => (
          <form
            onSubmit={handleSubmit}
            className="associate-users-units__form-container">
            <Field
              className="visually-hidden"
              name="unit"
              component="input"
              type="text"
            />
            <Field
              className="visually-hidden"
              name="initialUnits"
              component="input"
              type="select"
            />
            <div className="form__section-container form__section-container--grid">
              <div className="form__item-container">
                <div className="align-center">
                  <label
                    className="text-medium text-bold form-item__field-message--required"
                    htmlFor="unitIds">
                    {selectedUnitsText}
                  </label>
                  <HelpIcon
                    onClick={() =>
                      selectedUnitsHelpTextRef.current.toggleHelpText()
                    }
                    toggled={showSelectedUnitsHelpText}
                    styles="margin-left-5"
                  />
                </div>
              </div>
            </div>
            <div className="width-100 margin-bottom-10">
              <InlineHelpText
                ref={selectedUnitsHelpTextRef}
                toggleFunction={toggleSelectedUnitsHelpText}
                message={selectedUnitsHelpText}
              />
            </div>
            <div className="form__section-container form__section-container--grid margin-bottom-10">
              {selectedUnitsTable(values)}
              <div className="table__header padding-10">
                <label className="text-medium text-bold" htmlFor="usersFilter">
                  {searchText}:
                </label>
                <input
                  type="text"
                  id="unitsFilter"
                  name="unitsFilter"
                  onChange={unitsSearchFilter}
                  className="message-table__filter-search-box margin-left-10"
                  value={unitsFilter}
                />
              </div>
              {unitsTable(values)}
            </div>
            <div className="form__section-container form__section-container--spread associate-users-units__form-buttons-container">
              <div className="form__items-group-container" />
              <div className="form__items-group-container">
                <Link
                  className="link link-text"
                  to={props.previous}
                  data-test="bulk-associate-parent-unit-cancel-link">
                  {cancelText}
                </Link>
                <Button
                  buttonFunction={buttonFunctions.submit}
                  text={submitText}
                  buttonType={buttonTypes.success}
                  buttonSize={buttonSizes.small}
                  buttonTextSize={buttonTextSizes.mediumSmall}
                  additionalStyles="no-margin margin-left-15"
                  disabled={pristine || invalid}
                />
              </div>
            </div>
          </form>
        )}
      />
    </React.Fragment>
  );
}

const mapStateToProps = (state, ownProps) => {
  const unitArray = Object.values(state.units.units);
  const unitObj = state.units.units[ownProps.unit];
  return {
    unitArray,
    unitObj,
  };
};
export default connect(mapStateToProps)(UnconnectedBulkEditChildUnitsForm);
